import { useCallback } from 'react';
import { ThreadType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { InboxView } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { SMSTask } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread as UnifiedThread, ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { SMSDataSharedUtils } from '@frontend/api-sms-data';
import { getUser } from '@frontend/auth-helpers';
import { Options } from '@frontend/fetch';
import { emitCallTagsExternalSync } from '@frontend/inbox-call-item-event-bus';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { SchemaQueryKey } from '@frontend/react-query-helpers';
import { CountThreadsStatusUpdate } from '../../types';
import { getThreadItemId, getThreadPersonPhone, getThreadIdFromThreadItem } from '../../utils/thread-items-utils';
import { useFetchGetUnifiedThreadDetails } from '../queries/use-get-unified-thread-details';
import { useFetchGetUnifiedThreadItem } from '../queries/use-get-unified-thread-item';
import { serviceName } from '../service';
import { ServiceQueries } from '../types';
import { GetUnifiedThreadIO, GetUnifiedThreadDetailsIO } from '../types';
import {
  listUnifiedThreadsThreadUpdater,
  orderListUnifiedThreadsThreadUpdatesByUpdateType,
  updateListUnifiedThreadsData,
} from './endpoint-updater-functions';
import { EndpointQueryData, ResourceUpdateOptions, ResourceUpdateResult, CachedEndpointData } from './types';
import {
  getAffectedCountThreadsPredicate,
  getCountModifier,
  useGetUnifiedThreadDetails,
  matchUnifiedThreadToSms,
  upsertThreadItemOntoUnifiedThread,
  upsertThreadItemOntoGetUnifiedThreadQuery,
  threadCouldBeInListUnifiedThreadsResponse,
} from './utils';

type UseUnifiedThreadUpdaterOptions = {
  httpOptions?: Options;
};

export const useUnifiedThreadUpdater = ({ httpOptions }: UseUnifiedThreadUpdaterOptions = {}) => {
  const queryUpdaters = useSchemaQueryUpdaters<ServiceQueries>(serviceName);
  const getUnifiedThreadDetails = useGetUnifiedThreadDetails({ httpOptions });
  const { isSmsMessageItem, isCallRecordItem } = SMSDataSharedUtils.threadItemsUtils;
  const fetchThreadDetails = useFetchGetUnifiedThreadDetails();
  const fetchGetUnifiedThreadItem = useFetchGetUnifiedThreadItem();
  const user = getUser();

  const updateThreadCounts = useCallback(
    async ({
      threadIds,
      locationId,
      newStatus,
      isOptimistic = false,
    }: {
      threadIds: string[];
      locationId: string;
      newStatus: CountThreadsStatusUpdate;
      isOptimistic?: boolean;
    }) => {
      const originalThreads = await Promise.allSettled(
        threadIds.map(async (threadId) => {
          const thread = await getUnifiedThreadDetails({
            threadId,
            locationId,
            excludeLastAutomated: false,
            cacheOnly: true,
          });
          return thread;
        })
      );

      const filteredOriginalThreads = originalThreads.reduce<UnifiedThread[]>((acc, result) => {
        if (result.status === 'fulfilled' && result.value) acc.push(result.value);
        return acc;
      }, []);

      const allThreadsAreCached = filteredOriginalThreads.length === threadIds.length;
      if (!allThreadsAreCached) {
        // If not all threads are cached, invalidate queries for non-optimistic updates
        if (!isOptimistic) {
          queryUpdaters.invalidateQueries({
            endpointName: 'CountThreads',
            queryFilters: {
              predicate: getAffectedCountThreadsPredicate({ newStatus, locationId }),
            },
          });
        }
        return { revertCacheUpdate: () => {} };
      }

      const originalQueries = queryUpdaters.getQueriesData<'CountThreads'>({
        endpointName: 'CountThreads',
        queryFilters: {
          predicate: getAffectedCountThreadsPredicate({ newStatus, locationId }),
        },
      });

      originalQueries.forEach(([queryKey]) => {
        const request = queryKey[2];
        queryUpdaters.updateQuery<'CountThreads'>({
          endpointName: 'CountThreads',
          queryFilters: {
            queryKey,
            exact: true,
          },
          updater: (oldData) => {
            const modifier = getCountModifier(request, newStatus);
            const newCount =
              modifier === 'none'
                ? oldData.count
                : oldData.count + (modifier === 'add' ? threadIds.length : -1 * threadIds.length);
            return {
              ...oldData,
              count: Math.max(0, newCount),
            };
          },
        });
      });

      return {
        revertCacheUpdate: () => {
          originalQueries.forEach(([queryKey, data]) => {
            queryUpdaters.updateQuery<'CountThreads'>({
              endpointName: 'CountThreads',
              queryFilters: {
                queryKey,
                exact: true,
              },
              updater: () => data,
            });
          });
        },
      };
    },
    [queryUpdaters, getUnifiedThreadDetails]
  );

  const upsertUnifiedThread = useCallback(
    async ({
      matchValues,
      matchFn,
      matchStrategy,
      updater,
      isOptimistic,
      preserveStatus,
    }: ResourceUpdateOptions<UnifiedThread>): Promise<ResourceUpdateResult> => {
      // Get unified thread details (equivalent to getThreadDetails for unified schema)
      const cachedUnifiedThread = await getUnifiedThreadDetails({
        threadId: matchValues.threadId,
        locationId: matchValues.locationId,
        cacheOnly: isOptimistic,
        excludeLastAutomated: false,
        matchStrategy,
        matchFn,
      });

      // Check if any ListUnifiedThreads query excludes automated messages
      const anyListUnifiedThreadsIsManualOnly = !!queryUpdaters.getQueriesData({
        endpointName: 'ListUnifiedThreads',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'ListUnifiedThreads'>({
            endpointName: 'ListUnifiedThreads',
            request: {
              unifiedFilter: {
                excludeAutomatedLastMessage: true,
              },
            },
          }),
          exact: false,
        },
      }).length;

      // Get cached thread excluding automated messages if needed
      const cachedManualUnifiedThread =
        cachedUnifiedThread?.items?.[0] &&
        isSmsMessageItem(cachedUnifiedThread.items[0]) &&
        cachedUnifiedThread.items[0].smsMessage?.autogeneratedBy
          ? anyListUnifiedThreadsIsManualOnly
            ? await getUnifiedThreadDetails({
                threadId: matchValues.threadId,
                locationId: matchValues.locationId,
                cacheOnly: isOptimistic,
                excludeLastAutomated: true,
                matchStrategy,
                matchFn,
              })
            : undefined
          : cachedUnifiedThread;

      // Apply the updater function to both cached threads
      const unifiedThreadFromUpdater = await updater(cachedUnifiedThread);
      const manualUnifiedThreadFromUpdater = await updater(cachedManualUnifiedThread);

      // Resolve manual thread (ensure it doesn't have automated first message)
      const resolvedManualUnifiedThreadFromUpdater =
        manualUnifiedThreadFromUpdater?.items?.[0] &&
        isSmsMessageItem(manualUnifiedThreadFromUpdater.items[0]) &&
        manualUnifiedThreadFromUpdater.items[0].smsMessage?.autogeneratedBy
          ? undefined
          : manualUnifiedThreadFromUpdater;

      // Ensure consistency between updated thread and its first SMS message
      let updatedUnifiedThread = unifiedThreadFromUpdater
        ? matchUnifiedThreadToSms({
            thread: unifiedThreadFromUpdater,
            originalSms: cachedUnifiedThread?.items?.find(isSmsMessageItem)?.smsMessage,
            updatedSms: unifiedThreadFromUpdater?.items?.find(isSmsMessageItem)?.smsMessage,
          })
        : undefined;

      let updatedExcludeAutomatedUnifiedThread = resolvedManualUnifiedThreadFromUpdater
        ? matchUnifiedThreadToSms({
            thread: resolvedManualUnifiedThreadFromUpdater,
            originalSms: cachedManualUnifiedThread?.items?.find(isSmsMessageItem)?.smsMessage,
            updatedSms: resolvedManualUnifiedThreadFromUpdater?.items?.find(isSmsMessageItem)?.smsMessage,
          })
        : undefined;

      // When preserveStatus is set, restore the cached thread's status/isReplied/actionable
      // so that the thread moves to the top of the list without changing its read state.
      if (preserveStatus) {
        if (updatedUnifiedThread && cachedUnifiedThread) {
          updatedUnifiedThread = {
            ...updatedUnifiedThread,
            status: cachedUnifiedThread.status,
            isReplied: cachedUnifiedThread.isReplied,
            actionable: cachedUnifiedThread.actionable,
          };
        }
        if (updatedExcludeAutomatedUnifiedThread && cachedManualUnifiedThread) {
          updatedExcludeAutomatedUnifiedThread = {
            ...updatedExcludeAutomatedUnifiedThread,
            status: cachedManualUnifiedThread.status,
            isReplied: cachedManualUnifiedThread.isReplied,
            actionable: cachedManualUnifiedThread.actionable,
          };
        }
      }

      // ListUnifiedThreads cache updates
      const originalListUnifiedThreadQueries: EndpointQueryData<'ListUnifiedThreads'>[] = [];

      if (!!updatedUnifiedThread || !!updatedExcludeAutomatedUnifiedThread) {
        const allListUnifiedThreadsQueries = queryUpdaters.getQueriesData<'ListUnifiedThreads', true>({
          endpointName: 'ListUnifiedThreads',
        });

        // Separate queries that exclude automated messages from others
        const { excludeLastAutomatedQueries, otherQueries } = allListUnifiedThreadsQueries.reduce<{
          excludeLastAutomatedQueries: typeof allListUnifiedThreadsQueries;
          otherQueries: typeof allListUnifiedThreadsQueries;
        }>(
          (acc, curr) => {
            const isExcludingLastAutomated = !!curr[0][2]?.unifiedFilter?.excludeAutomatedLastMessage;
            if (isExcludingLastAutomated) {
              acc.excludeLastAutomatedQueries.push(curr);
            } else {
              acc.otherQueries.push(curr);
            }
            return acc;
          },
          { excludeLastAutomatedQueries: [], otherQueries: [] }
        );

        // Update queries that exclude automated messages (if we have non-automated thread)
        if (!!updatedExcludeAutomatedUnifiedThread) {
          const orderedUpdates = orderListUnifiedThreadsThreadUpdatesByUpdateType(excludeLastAutomatedQueries, {
            matchValues,
            matchFn,
            matchStrategy,
            originalResource: cachedManualUnifiedThread,
            updatedResource: updatedExcludeAutomatedUnifiedThread,
          });

          orderedUpdates.forEach(({ queryData, updateType }) => {
            if (updateType === 'none') return;

            const filteredTags = queryData[0][2]?.unifiedFilter?.inboxTags ?? [];
            const updatedData = listUnifiedThreadsThreadUpdater(
              {
                matchValues,
                matchFn,
                matchStrategy,
                originalResource: cachedManualUnifiedThread,
                updatedResource: filteredTags.length
                  ? {
                      ...updatedExcludeAutomatedUnifiedThread,
                      // For unified threads, uniqueTags are InboxTags with entityId instead of SMSTags with smsId
                      uniqueTags: updatedExcludeAutomatedUnifiedThread.uniqueTags?.filter((tag) =>
                        filteredTags.includes(tag.tagId)
                      ),
                    }
                  : updatedExcludeAutomatedUnifiedThread,
                queryData,
              },
              updateType
            );

            if (updatedData) {
              originalListUnifiedThreadQueries.push([queryData[0], structuredClone(queryData[1])]);
              queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
                endpointName: 'ListUnifiedThreads',
                queryFilters: {
                  queryKey: queryData[0],
                  exact: true,
                },
                updater: () => updatedData,
              });
            }
          });
        }

        // Update other queries (if we have updated thread)
        if (!!updatedUnifiedThread) {
          const orderedUpdates = orderListUnifiedThreadsThreadUpdatesByUpdateType(otherQueries, {
            matchValues,
            matchFn,
            matchStrategy,
            originalResource: cachedUnifiedThread,
            updatedResource: updatedUnifiedThread,
          });

          orderedUpdates.forEach(({ queryData, updateType }) => {
            if (updateType === 'none') return;

            const filteredTags = queryData[0][2]?.unifiedFilter?.inboxTags ?? [];
            const updatedData = listUnifiedThreadsThreadUpdater(
              {
                matchValues,
                matchFn,
                matchStrategy,
                originalResource: cachedUnifiedThread,
                updatedResource: filteredTags.length
                  ? {
                      ...updatedUnifiedThread,
                      // For unified threads, uniqueTags are InboxTags with entityId instead of SMSTags with smsId
                      uniqueTags: updatedUnifiedThread.uniqueTags?.filter((tag) => filteredTags.includes(tag.tagId)),
                    }
                  : updatedUnifiedThread,
                queryData,
              },
              updateType
            );

            if (updatedData) {
              originalListUnifiedThreadQueries.push([queryData[0], structuredClone(queryData[1])]);
              queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
                endpointName: 'ListUnifiedThreads',
                queryFilters: {
                  queryKey: queryData[0],
                  exact: true,
                },
                updater: () => updatedData,
              });
            }
          });
        }
      }

      // GetUnifiedThreadDetails

      const originalGetUnifiedThreadDetailsQueries: EndpointQueryData<'GetUnifiedThreadDetails'>[] = [];

      // Never update GetUnifiedThreadDetails if updating optimistically since it's really only used to update the cache
      if (!isOptimistic) {
        const applicableGetUnifiedThreadDetailsQueries = queryUpdaters.getQueriesData({
          endpointName: 'GetUnifiedThreadDetails',
          queryFilters: {
            queryKey: queryUpdaters.getQueryKey<'GetUnifiedThreadDetails'>({
              endpointName: 'GetUnifiedThreadDetails',
              request: {
                threadId: matchValues.threadId,
                locationId: matchValues.locationId,
              },
            }),
            exact: false,
            predicate: !resolvedManualUnifiedThreadFromUpdater
              ? ({ queryKey }) => !queryKey[2]?.excludeAutomatedLastMessage
              : undefined,
          },
        });

        applicableGetUnifiedThreadDetailsQueries.forEach((queryData) => {
          const excludeLastAutomated = queryData[0][2]?.excludeAutomatedLastMessage;
          if (excludeLastAutomated && updatedExcludeAutomatedUnifiedThread) {
            originalGetUnifiedThreadDetailsQueries.push([queryData[0], structuredClone(queryData[1])]);
            queryUpdaters.updateQuery({
              endpointName: 'GetUnifiedThreadDetails',
              queryFilters: {
                queryKey: queryData[0],
              },
              updater: () => ({ thread: updatedExcludeAutomatedUnifiedThread }),
            });
          } else if (!excludeLastAutomated && updatedUnifiedThread) {
            originalGetUnifiedThreadDetailsQueries.push([queryData[0], structuredClone(queryData[1])]);
            queryUpdaters.updateQuery({
              endpointName: 'GetUnifiedThreadDetails',
              queryFilters: {
                queryKey: queryData[0],
              },
              updater: () => ({ thread: updatedUnifiedThread }),
            });
          }
        });
      }

      // GetUnifiedThread

      const originalGetUnifiedThreadQueries: EndpointQueryData<'GetUnifiedThread'>[] = [];

      // Never update GetUnifiedThread if updating optimistically since it's really only used to update the cache
      if (!isOptimistic && updatedUnifiedThread) {
        const applicableGetUnifiedThreadQueries = queryUpdaters.getQueriesData<'GetUnifiedThread', true>({
          endpointName: 'GetUnifiedThread',
          queryFilters: {
            queryKey: queryUpdaters.getQueryKey<'GetUnifiedThread'>({
              endpointName: 'GetUnifiedThread',
              request: {
                threadId: matchValues.threadId,
                locationId: matchValues.locationId,
              },
            }),
            exact: false,
          },
        });

        applicableGetUnifiedThreadQueries.forEach(([queryKey, data]) => {
          originalGetUnifiedThreadQueries.push([queryKey, structuredClone(data)]);

          queryUpdaters.updateQuery<'GetUnifiedThread', true>({
            endpointName: 'GetUnifiedThread',
            queryFilters: {
              queryKey,
              exact: true,
            },
            updater: (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                pages: oldData.pages.map((oldPage) => {
                  // Preserve page-specific items while updating thread-level fields
                  const { items: _updatedItems, ...threadLevelFields } = updatedUnifiedThread;

                  return {
                    ...oldPage,
                    thread: {
                      ...oldPage.thread,
                      ...threadLevelFields,
                      // If updatedUnifiedThread has aiStatus, merge that
                      ...(updatedUnifiedThread.aiStatus && {
                        aiStatus: {
                          ...oldPage.thread.aiStatus,
                          ...updatedUnifiedThread.aiStatus,
                        },
                      }),
                      // Preserve the page-specific items array
                      items: oldPage.thread.items,
                    },
                  };
                }),
              };
            },
          });
        });
      }

      return {
        revertCacheUpdate: () => {
          // Revert ListUnifiedThreads changes
          originalListUnifiedThreadQueries.forEach(([queryKey, queryData]) => {
            queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
              endpointName: 'ListUnifiedThreads',
              queryFilters: { queryKey, exact: true },
              updater: () => queryData,
            });
          });
          // Revert GetUnifiedThreadDetails changes
          originalGetUnifiedThreadDetailsQueries.forEach(([queryKey, queryData]) => {
            queryUpdaters.updateQuery({
              endpointName: 'GetUnifiedThreadDetails',
              queryFilters: { queryKey, exact: true },
              updater: () => queryData,
            });
          });
          // Revert GetUnifiedThread changes
          originalGetUnifiedThreadQueries.forEach(([queryKey, queryData]) => {
            queryUpdaters.updateQuery<'GetUnifiedThread', true>({
              endpointName: 'GetUnifiedThread',
              queryFilters: { queryKey, exact: true },
              updater: () => queryData,
            });
          });
        },
      };
    },
    [queryUpdaters, getUnifiedThreadDetails]
  );

  const upsertThreadItem = useCallback(
    async ({
      matchValues,
      updater,
      isOptimistic,
      preserveStatus,
      getThreadOnly,
      updateOnly,
    }: ResourceUpdateOptions<ThreadItem> & {
      getThreadOnly?: boolean;
      updateOnly?: boolean;
    }): Promise<ResourceUpdateResult> => {
      let upsertThreadResult: ResourceUpdateResult | undefined = undefined;

      if (!getThreadOnly) {
        upsertThreadResult = await upsertUnifiedThread({
          matchValues: {
            threadId: matchValues.threadId,
            locationId: matchValues.locationId,
          },
          updater: async (thread) => {
            if (!thread) {
              return undefined;
            }

            const matchingThreadItem = thread.items?.find((item) => getThreadItemId(item) === matchValues.threadItemId);

            const updatedThreadItem = await updater(matchingThreadItem);

            if (!updatedThreadItem) return undefined;

            return upsertThreadItemOntoUnifiedThread(thread, updatedThreadItem);
          },
          isOptimistic,
          preserveStatus,
        });
      }

      // Handle GetUnifiedThread
      let allApplicableGetUnifiedThreadQueries = queryUpdaters.getQueriesData<'GetUnifiedThread', true>({
        endpointName: 'GetUnifiedThread',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetUnifiedThread'>({
            endpointName: 'GetUnifiedThread',
            request: {
              threadId: matchValues.threadId,
              locationId: matchValues.locationId,
            },
          }),
          exact: false,
        },
      });

      let cachedThreadItem: ThreadItem | undefined = undefined;
      if (allApplicableGetUnifiedThreadQueries.length) {
        for (const query of allApplicableGetUnifiedThreadQueries) {
          if (cachedThreadItem) break;
          const queryData = query?.[1];
          const pages = queryData?.pages;
          if (pages) {
            for (const page of pages) {
              if (cachedThreadItem) break;
              cachedThreadItem = page.thread?.items?.find((item) => getThreadItemId(item) === matchValues.threadItemId);
            }
          }
        }
      }

      let updatedThreadItem = await updater(cachedThreadItem);

      if (!updatedThreadItem && !updateOnly && matchValues.entityType) {
        let fetchedThreadItem: ThreadItem | undefined;
        try {
          const fetchedThreadItemResponse = await fetchGetUnifiedThreadItem(
            {
              itemId: matchValues.threadItemId,
              locationId: matchValues.locationId,
              entityType: matchValues.entityType,
            },
            {
              cacheTime: 0,
              staleTime: 0,
              retry: 1,
            },
            {
              ...httpOptions,
              skipRetry: true,
            }
          );

          fetchedThreadItem = fetchedThreadItemResponse?.threadItem;
          const fetchedThreadId = getThreadIdFromThreadItem(fetchedThreadItem);

          // For when threadId is not available but threadItemId is present
          if (fetchedThreadId && !allApplicableGetUnifiedThreadQueries.length) {
            allApplicableGetUnifiedThreadQueries = queryUpdaters.getQueriesData<'GetUnifiedThread', true>({
              endpointName: 'GetUnifiedThread',
              queryFilters: {
                queryKey: queryUpdaters.getQueryKey<'GetUnifiedThread'>({
                  endpointName: 'GetUnifiedThread',
                  request: {
                    threadId: fetchedThreadId,
                    locationId: matchValues.locationId,
                  },
                }),
                exact: false,
              },
            });
          }
        } catch {
          fetchedThreadItem = undefined;
        }

        updatedThreadItem = isOptimistic ? await updater(fetchedThreadItem) : fetchedThreadItem;
      }

      const originalGetUnifiedThreadQueries: EndpointQueryData<'GetUnifiedThread'>[] = [];
      if (updatedThreadItem) {
        // Store original data before updates
        allApplicableGetUnifiedThreadQueries.forEach((queryData) => {
          originalGetUnifiedThreadQueries.push([queryData[0], structuredClone(queryData[1])]);
        });

        // Now perform the updates
        allApplicableGetUnifiedThreadQueries.forEach(([queryKey]) => {
          queryUpdaters.updateQuery<'GetUnifiedThread', true>({
            endpointName: 'GetUnifiedThread',
            queryFilters: {
              queryKey,
              exact: true,
            },
            updater: (oldData) => {
              if (!oldData) return oldData;

              const [, newData] = upsertThreadItemOntoGetUnifiedThreadQuery({
                query: [queryKey, oldData],
                newThreadItem: updatedThreadItem,
                matchingThreadItemId: matchValues.threadItemId ?? '',
              });

              // When preserveStatus is set, restore page-level status/isReplied/actionable
              // from the pre-update data so the thread doesn't get marked unread/unreplied.
              if (preserveStatus) {
                return {
                  ...newData,
                  pages: newData.pages.map((page, i) => ({
                    ...page,
                    thread: {
                      ...page.thread,
                      status: oldData.pages[i]?.thread.status ?? page.thread.status,
                      isReplied: oldData.pages[i]?.thread.isReplied ?? page.thread.isReplied,
                      actionable: oldData.pages[i]?.thread.actionable ?? page.thread.actionable,
                    },
                  })),
                };
              }

              return newData;
            },
          });
        });
      }

      return {
        revertCacheUpdate: () => {
          upsertThreadResult?.revertCacheUpdate();
          if (updatedThreadItem) {
            originalGetUnifiedThreadQueries.forEach(([queryKey, originalData]) => {
              queryUpdaters.updateQuery<'GetUnifiedThread', true>({
                endpointName: 'GetUnifiedThread',
                queryFilters: { queryKey, exact: true },
                updater: () => originalData,
              });
            });
          }
        },
      };
    },
    [upsertUnifiedThread, queryUpdaters, fetchGetUnifiedThreadItem, httpOptions]
  );

  const deleteThreadItem = useCallback(
    async ({
      matchValues,
      deletedAt = new Date().toISOString(),
      deletedBy = user?.userID ?? '',
      statusDetails,
      isOptimistic,
    }: Omit<ResourceUpdateOptions<ThreadItem>, 'updater' | 'isAutomated' | 'matchFn' | 'matchStrategy'> & {
      deletedAt?: string;
      deletedBy?: string;
      statusDetails?: string;
    }): Promise<ResourceUpdateResult> => {
      let updatedThread: UnifiedThread | undefined;

      if (!isOptimistic) {
        queryUpdaters.invalidateQueries({
          endpointName: 'GetUnifiedThreadDetails',
          queryFilters: {
            queryKey: queryUpdaters.getQueryKey<'GetUnifiedThreadDetails'>({
              endpointName: 'GetUnifiedThreadDetails',
              request: {
                threadId: matchValues.threadId,
                locationId: matchValues.locationId,
              },
            }),
            exact: false,
          },
        });
        try {
          const response = await fetchThreadDetails({
            request: {
              threadId: matchValues.threadId,
              locationId: matchValues.locationId,
            },
          });
          if (response?.thread?.id) {
            updatedThread = response.thread;
          }
        } catch (error) {
          console.error('Error fetching unified thread details:', error);
        }
      }

      let anyQueryExcludesAutomated = false;
      const queriesToUpdate = queryUpdaters.getQueriesData<'ListUnifiedThreads', true>({
        endpointName: 'ListUnifiedThreads',
        queryFilters: {
          exact: false,
          predicate: ({ queryKey, state: { data } }) => {
            const request = queryKey[2];
            const matchesLocations = request.locationIds?.includes(matchValues.locationId);
            if (!matchesLocations) return false;

            if (updatedThread && request.locationIds?.length) {
              const typedRequest = request as Compulsory<typeof request, 'locationIds'>;
              const updatedThreadShouldBeInQuery = threadCouldBeInListUnifiedThreadsResponse({
                thread: updatedThread,
                listUnifiedThreadsRequest: typedRequest,
              });
              if (updatedThreadShouldBeInQuery) return true;
            }

            const hasThreadWithItem = !!data?.pages.some((page) =>
              page.threads.some((thread) => {
                const firstItem = thread.items?.[0];
                if (!firstItem) return false;
                const isMatchingThread =
                  thread.id === matchValues.threadId && getThreadItemId(firstItem) === matchValues.threadItemId;
                if (!updatedThread && isOptimistic) {
                  // Create optimistic updated thread for the cache
                  const threadItemToDelete = thread.items?.find(
                    (item) => getThreadItemId(item) === matchValues.threadItemId
                  );
                  if (threadItemToDelete && isSmsMessageItem(threadItemToDelete)) {
                    const updatedThreadItem = {
                      ...threadItemToDelete,
                      smsMessage: {
                        ...threadItemToDelete.smsMessage!,
                        deletedAt,
                        deletedBy,
                        statusDetails:
                          statusDetails !== undefined ? statusDetails : threadItemToDelete.smsMessage!.statusDetails,
                        tags: [],
                        tagsDetailed: [],
                        tasks: [],
                      },
                    };
                    updatedThread = {
                      ...thread,
                      items:
                        thread.items?.map((item) =>
                          getThreadItemId(item) === matchValues.threadItemId ? updatedThreadItem : item
                        ) || [],
                    };
                  }
                }
                return isMatchingThread;
              })
            );
            if (hasThreadWithItem && request.unifiedFilter?.excludeAutomatedLastMessage) {
              anyQueryExcludesAutomated = true;
            }
            return hasThreadWithItem;
          },
        },
      });

      if (!queriesToUpdate.length) {
        return {
          revertCacheUpdate: () => {},
        };
      }

      if (!updatedThread) {
        return {
          revertCacheUpdate: () => {},
        };
      }

      // Handle automated message exclusion logic
      let updatedThreadWithExcludedAutomated: UnifiedThread | undefined =
        updatedThread.items?.[0] &&
        isSmsMessageItem(updatedThread.items[0]) &&
        updatedThread.items[0].smsMessage?.autogeneratedBy
          ? undefined
          : updatedThread;

      if (anyQueryExcludesAutomated && !updatedThreadWithExcludedAutomated) {
        try {
          const response = await fetchThreadDetails({
            request: {
              threadId: matchValues.threadId,
              locationId: matchValues.locationId,
              excludeAutomatedLastMessage: true,
            },
          });
          updatedThreadWithExcludedAutomated = response.thread;
        } catch (error) {
          console.error('Error fetching unified thread details with excluded automated:', error);
        }
      }

      // Update ListUnifiedThreads queries
      queriesToUpdate.forEach(([queryKey]) => {
        queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
          endpointName: 'ListUnifiedThreads',
          queryFilters: {
            queryKey,
            exact: true,
          },
          updater: (oldData, queryKey) => {
            const request = queryKey[2];
            const excludeLastAutomated = !!request.unifiedFilter?.excludeAutomatedLastMessage;

            if (!isOptimistic) {
              const threadToUse = excludeLastAutomated ? updatedThreadWithExcludedAutomated : updatedThread;
              if (!threadToUse) return oldData;

              return (
                updateListUnifiedThreadsData({
                  matchValues: {
                    threadId: matchValues.threadId,
                    locationId: matchValues.locationId,
                  },
                  queryData: [queryKey, oldData],
                  updatedResource: threadToUse,
                  inPlace: false,
                }) ?? oldData
              );
            } else {
              // Optimistic update
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  threads: page.threads.map((thread) =>
                    thread.id === matchValues.threadId
                      ? {
                          ...thread,
                          items:
                            thread.items?.map((item) =>
                              getThreadItemId(item) === matchValues.threadItemId
                                ? isSmsMessageItem(item)
                                  ? {
                                      ...item,
                                      smsMessage: {
                                        ...item.smsMessage!,
                                        deletedAt,
                                        deletedBy,
                                        statusDetails:
                                          statusDetails !== undefined ? statusDetails : item.smsMessage!.statusDetails,
                                        tags: [],
                                        tagsDetailed: [],
                                        tasks: [],
                                      },
                                    }
                                  : item
                                : item
                            ) || [],
                          // TODO: update tasks and uniqueTags for optimistic flow,
                        }
                      : thread
                  ),
                })),
              };
            }
          },
        });
      });

      // Update GetUnifiedThread queries similar to upsertSms logic
      const upsertThreadItemResult = await upsertThreadItem({
        matchValues: {
          threadItemId: matchValues.threadItemId,
          threadId: matchValues.threadId,
          locationId: matchValues.locationId,
        },
        updateOnly: true,
        isOptimistic,
        getThreadOnly: true,
        updater: (threadItem) => {
          if (!threadItem) return undefined;

          if (isSmsMessageItem(threadItem)) {
            return {
              ...threadItem,
              smsMessage: {
                ...threadItem.smsMessage!,
                tags: [],
                tagsDetailed: [],
                tasks: [],
                deletedAt,
                deletedBy,
                statusDetails: statusDetails !== undefined ? statusDetails : threadItem.smsMessage!.statusDetails,
              },
            };
          } else if (isCallRecordItem(threadItem)) {
            return {
              ...threadItem,
              callRecord: {
                ...threadItem.callRecord!,
                tags: [],
                deletedAt,
                deletedBy,
              },
            };
          }
          return threadItem;
        },
      });

      // TODO: implement CountThreads similar to original deleteSms

      return {
        revertCacheUpdate: () => {
          queriesToUpdate.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
              endpointName: 'ListUnifiedThreads',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });
          upsertThreadItemResult.revertCacheUpdate();
        },
      };
    },
    [fetchThreadDetails, queryUpdaters, upsertThreadItem, user?.userID, isSmsMessageItem, isCallRecordItem]
  );

  const togglePersonPhoneBlockUnified = useCallback(
    ({
      personPhone,
      isBlocked,
      locationId,
      isOptimistic,
    }: {
      personPhone: string;
      isBlocked: boolean;
      locationId: string;
      isOptimistic: boolean;
    }) => {
      // ListUnifiedThreads
      const allListUnifiedThreadsQueries = queryUpdaters.getQueriesData<'ListUnifiedThreads', true>({
        endpointName: 'ListUnifiedThreads',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            const locationIdMatches = !!request.locationIds?.includes(locationId);

            if (!locationIdMatches) return false;
            if (
              request.unifiedFilter?.personPhones?.length &&
              !request.unifiedFilter.personPhones.some(
                (filterPersonPhone) => formatPhoneNumberE164(filterPersonPhone) === formatPhoneNumberE164(personPhone)
              )
            ) {
              return false;
            }
            return true;
          },
        },
      });

      const { queriesToFilter, queriesToInvalidate } = allListUnifiedThreadsQueries.reduce<{
        queriesToFilter: typeof allListUnifiedThreadsQueries;
        queriesToInvalidate: typeof allListUnifiedThreadsQueries;
      }>(
        (acc, curr) => {
          const isBlockedInboxView =
            (curr[0][2].unifiedFilter?.inboxView ?? InboxView.UNSPECIFIED) === InboxView.BLOCKED;
          const viewCanBeFiltered = isBlockedInboxView !== isBlocked;

          if (viewCanBeFiltered) {
            acc.queriesToFilter.push(curr);
          } else {
            acc.queriesToInvalidate.push(curr);
          }

          return acc;
        },
        { queriesToFilter: [], queriesToInvalidate: [] }
      );

      queriesToFilter.forEach(([queryKey]) => {
        queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
          endpointName: 'ListUnifiedThreads',
          queryFilters: {
            queryKey,
            exact: true,
          },
          updater: (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                threads: page.threads.filter((thread) => {
                  if (thread.locationId !== locationId) return true;
                  if (thread.threadType === ThreadType.THREAD_TYPE_GROUP_MMS) return true;

                  // For unified threads, get personPhone from first SMS item in items array
                  const firstSmsItem = thread.items?.find(isSmsMessageItem);
                  const threadPersonPhone = firstSmsItem?.smsMessage?.personPhone;
                  if (!threadPersonPhone) return true;

                  return formatPhoneNumberE164(threadPersonPhone) !== formatPhoneNumberE164(personPhone);
                }),
              })),
            };
          },
        });
      });

      if (!isOptimistic) {
        queriesToInvalidate.forEach(([queryKey]) => {
          queryUpdaters.invalidateQueries({
            endpointName: 'ListUnifiedThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
          });
        });
      }

      const originalGetUnifiedThreadQueries: EndpointQueryData<'GetUnifiedThread'>[] = [];
      queryUpdaters.updateQuery<'GetUnifiedThread', true>({
        endpointName: 'GetUnifiedThread',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetUnifiedThread'>({
            endpointName: 'GetUnifiedThread',
            request: {
              locationId,
            },
          }),
          exact: false,
          predicate: ({ queryKey, state: { data } }) => {
            const isGroupThread = !!data?.pages?.some(
              (page) => page.thread?.threadType === ThreadType.THREAD_TYPE_GROUP_MMS
            );

            if (isGroupThread) {
              return false;
            }

            // For unified threads, check SMS messages within ThreadItems across all pages
            const personPhoneMatches = !!data?.pages?.some((page) =>
              page.thread.items?.some((threadItem) => {
                if (!isSmsMessageItem(threadItem)) return false;
                return (
                  formatPhoneNumberE164(threadItem.smsMessage?.personPhone || '') === formatPhoneNumberE164(personPhone)
                );
              })
            );

            if (personPhoneMatches && !!data) {
              originalGetUnifiedThreadQueries.push([queryKey, data]);
            }
            return personPhoneMatches;
          },
        },
        updater: (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              threads: page.thread.items.map((threadItem) => {
                // Check if this thread has messages from the person phone
                const hasMatchingPersonPhone =
                  isSmsMessageItem(threadItem) &&
                  formatPhoneNumberE164(threadItem.smsMessage?.personPhone || '') ===
                    formatPhoneNumberE164(personPhone);

                if (hasMatchingPersonPhone) {
                  return {
                    ...threadItem,
                    isBlocked,
                  };
                }

                return threadItem;
              }),
            })),
          };
        },
      });

      if (!isOptimistic) {
        // Wait for BE to be ready to have a new count after blocking/unblocking
        setTimeout(() => {
          queryUpdaters.invalidateQueries({
            endpointName: 'CountThreads',
          });
        }, 500);
      }

      return {
        revertCacheUpdate: () => {
          queriesToFilter.forEach(([queryKey, queryData]) => {
            queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
              endpointName: 'ListUnifiedThreads',
              queryFilters: { queryKey, exact: true },
              updater: () => queryData,
            });
          });
          originalGetUnifiedThreadQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'GetUnifiedThread', true>({
              endpointName: 'GetUnifiedThread',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });
        },
      };
    },
    [queryUpdaters]
  );

  const updateUnifiedThreadsWithNewPerson = useCallback(
    ({
      personPhones,
      personId,
      firstName,
      lastName,
      preferredName,
      onlyEmptyPersons,
      locationId,
    }: {
      personPhones: string[];
      personId: string;
      firstName: string;
      lastName: string;
      preferredName: string;
      onlyEmptyPersons: boolean;
      locationId: string;
    }) => {
      const formattedPersonPhones = personPhones.map(formatPhoneNumberE164);

      // Update ListUnifiedThreads queries
      queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
        endpointName: 'ListUnifiedThreads',
        updater: (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              threads: page.threads.map((thread) => {
                if (thread.locationId !== locationId) return thread;
                if (thread.threadType === ThreadType.THREAD_TYPE_GROUP_MMS) return thread;
                if (!!thread.person?.personId && onlyEmptyPersons) return thread;

                // For unified threads, get personPhone from first SMS item in items array
                const firstSmsItem = thread.items?.find(isSmsMessageItem);
                const threadPhone = firstSmsItem?.smsMessage?.personPhone;
                if (!threadPhone) return thread;

                const formattedThreadPhone = formatPhoneNumberE164(threadPhone);
                if (formattedPersonPhones.includes(formattedThreadPhone)) {
                  return {
                    ...thread,
                    person: {
                      personId,
                      firstName,
                      lastName,
                      preferredName,
                    },
                    // Update items array - update SMS messages within ThreadItems
                    items:
                      thread.items?.map((item) => {
                        if (!isSmsMessageItem(item)) return item;

                        const smsMessage = item.smsMessage;
                        if (!smsMessage) return item;

                        // Only update if this SMS matches one of the person phones and meets the onlyEmpty criteria
                        if (smsMessage.personId && onlyEmptyPersons) return item;

                        if (formattedPersonPhones.includes(formatPhoneNumberE164(smsMessage.personPhone))) {
                          return {
                            ...item,
                            smsMessage: {
                              ...smsMessage,
                              personId,
                            },
                          };
                        }

                        return item;
                      }) || [],
                  } satisfies typeof thread;
                }

                return thread;
              }),
            })),
          };
        },
      });

      // Update GetUnifiedThread queries (paginated equivalent to GetThreadDetails)
      queryUpdaters.updateQuery<'GetUnifiedThread', true>({
        endpointName: 'GetUnifiedThread',
        updater: (oldData) => {
          if (!oldData) return oldData;
          const thread = oldData.pages[0]?.thread;
          if (thread?.locationId !== locationId) return oldData;
          if (thread?.threadType === ThreadType.THREAD_TYPE_GROUP_MMS) return oldData;
          if (thread?.person?.personId && onlyEmptyPersons) return oldData;

          const threadPhone = getThreadPersonPhone(thread.items[0]);
          if (!threadPhone) return oldData;

          const formattedThreadPhone = formatPhoneNumberE164(threadPhone);
          if (formattedPersonPhones.includes(formattedThreadPhone)) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                thread: {
                  ...page.thread,
                  person: {
                    personId,
                    firstName,
                    lastName,
                    preferredName,
                  },
                },
              })),
            };
          }
          return oldData;
        },
      });
    },
    [queryUpdaters]
  );

  const updateUnifiedThreadsTasksList = useCallback(
    ({
      threadId,
      locationId,
      task,
      action,
    }: {
      threadId: string;
      locationId: string;
      task: SMSTask;
      action: 'UPDATE' | 'DELETE';
      isOptimistic?: boolean;
    }) => {
      const updateTasksArray = (existingTasks: SMSTask[] = [], newTask: SMSTask, updateAction: 'UPDATE' | 'DELETE') => {
        if (updateAction === 'DELETE') {
          return existingTasks.filter((existingTask) => existingTask.taskId !== newTask.taskId);
        }

        const existingTaskIndex = existingTasks.findIndex((existingTask) => existingTask.taskId === newTask.taskId);
        if (existingTaskIndex >= 0) {
          return existingTasks.map((existingTask) => (existingTask.taskId === newTask.taskId ? newTask : existingTask));
        } else {
          return [...existingTasks, newTask];
        }
      };

      // Helper function to check if thread should be visible based on task status filter
      const shouldThreadBeVisibleForTaskStatus = (
        updatedThread: { tasks?: SMSTask[] },
        taskStatusFilter?: string[]
      ): boolean => {
        if (!taskStatusFilter || taskStatusFilter.length === 0) {
          return true; // No task status filter applied
        }

        const threadTasks = updatedThread.tasks || [];
        if (threadTasks.length === 0) {
          return false; // Thread has no tasks, shouldn't be visible when task filter is applied
        }

        // Check if any of the thread's tasks match the filtered task statuses
        return threadTasks.some((threadTask) => taskStatusFilter.includes(threadTask.status));
      };

      // Get all ListUnifiedThreads queries that might be affected
      const originalListUnifiedThreadsQueries: EndpointQueryData<'ListUnifiedThreads'>[] = [];
      const listUnifiedThreadsQueries = queryUpdaters.getQueriesData<'ListUnifiedThreads', true>({
        endpointName: 'ListUnifiedThreads',
      });

      // Update ListUnifiedThreads queries with smart filtering
      listUnifiedThreadsQueries.forEach(([queryKey, data]) => {
        if (!data) return;

        const queryRequest = queryKey[2];
        const taskStatusFilter = queryRequest.unifiedFilter?.taskStatuses;

        // Check if this query affects our location
        if (!queryRequest.locationIds?.includes(locationId)) {
          return;
        }

        // Find if thread exists in current query data
        const threadExistsInQuery = data.pages.some((page) => page.threads.some((thread) => thread.id === threadId));

        // For DELETE action, also check if the deleted task's status would affect this filter
        const deletedTaskMightAffectThisFilter = action === 'DELETE' && taskStatusFilter?.includes(task.status);

        if (threadExistsInQuery || deletedTaskMightAffectThisFilter) {
          // Thread exists in query OR deleted task might affect this filter, update it
          originalListUnifiedThreadsQueries.push([queryKey, data]);

          queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
            endpointName: 'ListUnifiedThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
            updater: (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  threads: page.threads.reduce<typeof page.threads>((acc, thread) => {
                    if (thread.id === threadId && thread.locationId === locationId) {
                      const updatedThread = {
                        ...thread,
                        tasks: updateTasksArray(thread.tasks, task, action),
                      };

                      // Check if updated thread should still be visible with current filters
                      if (shouldThreadBeVisibleForTaskStatus(updatedThread, taskStatusFilter)) {
                        acc.push(updatedThread);
                      }
                      // If thread should no longer be visible, it's filtered out (not added to acc)
                    } else {
                      acc.push(thread);
                    }
                    return acc;
                  }, []),
                })),
              };
            },
          });
        } else if (action === 'UPDATE') {
          // Thread doesn't exist in current query, but we're updating/adding a task
          // It might now be visible due to the task update
          // Invalidate this query to fetch fresh data
          queryUpdaters.invalidateQueries<'ListUnifiedThreads', true>({
            endpointName: 'ListUnifiedThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
          });
        } else if (action === 'DELETE' && taskStatusFilter?.length) {
          // For DELETE actions on filtered views, invalidate to ensure thread removal
          // This handles cases where thread should be removed from views it's not currently cached in
          queryUpdaters.invalidateQueries<'ListUnifiedThreads', true>({
            endpointName: 'ListUnifiedThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
          });
        }
      });

      // Update GetUnifiedThread queries (paginated equivalent to GetThreadDetails)
      const originalGetUnifiedThreadQueries: EndpointQueryData<'GetUnifiedThread'>[] = [];

      queryUpdaters.updateQuery<'GetUnifiedThread', true>({
        endpointName: 'GetUnifiedThread',
        queryFilters: {
          predicate: ({ queryKey, state: { data } }) => {
            // Check if any page contains the thread we're looking for
            const threadExists = !!data?.pages?.some(
              (page) => page.thread?.id === threadId && page.thread?.locationId === locationId
            );

            if (threadExists && !!data) {
              originalGetUnifiedThreadQueries.push([queryKey, data]);
            }

            return threadExists;
          },
        },
        updater: (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => {
              if (page.thread?.id === threadId && page.thread?.locationId === locationId) {
                return {
                  ...page,
                  thread: {
                    ...page.thread,
                    tasks: updateTasksArray(page.thread.tasks, task, action),
                  },
                };
              }
              return page;
            }),
          };
        },
      });

      // Update thread counts that might be affected by task status changes
      queryUpdaters.invalidateQueries<'CountThreads'>({
        endpointName: 'CountThreads',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            const hasTaskStatusFilter = request.filter?.taskStatuses?.length;
            const matchesLocation = request.locationIds?.includes(locationId);

            // For DELETE actions, invalidate all task-filtered count queries for this location
            // For UPDATE actions, only invalidate if task status filter is present
            if (action === 'DELETE') {
              return !!hasTaskStatusFilter && !!matchesLocation;
            } else {
              return !!hasTaskStatusFilter && !!matchesLocation;
            }
          },
        },
      });

      return {
        revertCacheUpdate: () => {
          // Revert ListUnifiedThreads changes
          originalListUnifiedThreadsQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'ListUnifiedThreads', true>({
              endpointName: 'ListUnifiedThreads',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });

          // Revert GetUnifiedThread changes
          originalGetUnifiedThreadQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'GetUnifiedThread', true>({
              endpointName: 'GetUnifiedThread',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });
        },
      };
    },
    [queryUpdaters]
  );

  const emitCallInsightsTagDismissSyncForVoiceItems = useCallback(
    ({ tagId, threadId, locationId }: { tagId: string; threadId: string; locationId: string }) => {
      const affectedCallRecordItems: Array<{
        channelId: string;
        originalTags: string[];
        updatedTags: string[];
        entityType?: EntityType;
      }> = [];

      // Find matching GetUnifiedThread queries with specific threadId and locationId that contain voice items with the tag
      const allGetUnifiedThreadQueries = queryUpdaters.getQueriesData<'GetUnifiedThread', true>({
        endpointName: 'GetUnifiedThread',
        queryFilters: {
          predicate: ({ queryKey, state: { data } }) => {
            const request = queryKey[2];

            // First check if threadId and locationId match
            const matchesThread = request?.threadId === threadId && request?.locationId === locationId;
            if (!matchesThread) {
              return false;
            }

            // Then check if any voice item in the cached pages contains the specified tag
            const containsVoiceItemWithTag = !!data?.pages?.some((page) =>
              page.thread?.items?.some((threadItem) => {
                if (isCallRecordItem(threadItem) && threadItem.callRecord) {
                  return threadItem.callRecord.tags?.some((tag) => tag.tagId === tagId);
                }
                return false;
              })
            );

            return containsVoiceItemWithTag;
          },
        },
      });

      // Process each query to find voice items with the tag
      allGetUnifiedThreadQueries.forEach(([, queryData]) => {
        if (queryData?.pages) {
          queryData.pages.forEach((page) => {
            if (page.thread?.items) {
              page.thread.items.forEach((threadItem) => {
                if (isCallRecordItem(threadItem) && threadItem.callRecord) {
                  const hasTag = threadItem.callRecord.tags?.some((tag) => tag.tagId === tagId);

                  if (hasTag && threadItem.callRecord.channelId) {
                    const originalTags = threadItem.callRecord.tags?.map((tag) => tag.tagId) || [];
                    const updatedTags =
                      threadItem.callRecord.tags?.filter((tag) => tag.tagId !== tagId).map((tag) => tag.tagId) || [];

                    affectedCallRecordItems.push({
                      channelId: threadItem.callRecord.channelId,
                      originalTags,
                      updatedTags,
                      entityType: threadItem.callRecord.entityType,
                    });
                  }
                }
              });
            }
          });
        }
      });

      // Emit call insights sync events for all affected call record items
      affectedCallRecordItems.forEach(({ channelId, updatedTags, entityType }) => {
        if (entityType === EntityType.ENTITY_TYPE_CALL) {
          emitCallTagsExternalSync({
            channelId,
            tagIds: updatedTags,
          });
        }
      });

      // Return a revert function to restore original tag states
      return {
        revertCacheUpdate: () => {
          // Emit call insights sync events with original tags to revert
          affectedCallRecordItems.forEach(({ channelId, originalTags, entityType }) => {
            if (entityType === EntityType.ENTITY_TYPE_CALL) {
              emitCallTagsExternalSync({
                channelId,
                tagIds: originalTags,
              });
            }
          });
        },
      };
    },
    [queryUpdaters, isCallRecordItem]
  );

  const removeTagFromUnifiedThread = useCallback(
    ({
      matchValues,
      tagId,
    }: {
      matchValues: {
        threadId: string;
        locationId: string;
      };
      tagId: string;
    }) => {
      // Store original query data for potential revert
      const originalQueries = queryUpdaters.getQueriesData<'GetUnifiedThread', true>({
        endpointName: 'GetUnifiedThread',
        queryFilters: {
          predicate: ({ queryKey, state: { data } }) => {
            const request = queryKey[2];

            // Match by threadId and locationId
            const matchesThread = request.threadId === matchValues.threadId;
            const matchesLocation = request.locationId === matchValues.locationId;

            if (!matchesThread || !matchesLocation) {
              return false;
            }

            // Check if any page contains thread items with the tag
            const containsTaggedItems = !!data?.pages?.some((page) =>
              page.thread?.items?.some((item) => {
                // Check SMS message items
                if (isSmsMessageItem(item) && item.smsMessage) {
                  return (
                    item.smsMessage.tags?.includes(tagId) ||
                    item.smsMessage.tagsDetailed?.some((tag) => tag.tagId === tagId)
                  );
                }

                // Check call record items
                if (isCallRecordItem(item) && item.callRecord) {
                  return item.callRecord.tags?.some((tag) => tag.tagId === tagId);
                }

                return false;
              })
            );

            return containsTaggedItems;
          },
        },
      });

      // Update the matching queries
      originalQueries.forEach(([queryKey]) => {
        queryUpdaters.updateQuery<'GetUnifiedThread', true>({
          endpointName: 'GetUnifiedThread',
          queryFilters: {
            queryKey,
            exact: true,
          },
          updater: (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page) => {
                if (!page.thread) return page;

                return {
                  ...page,
                  thread: {
                    ...page.thread,
                    // Remove tag from thread-level uniqueTags
                    uniqueTags: page.thread.uniqueTags?.filter((tag) => tag.tagId !== tagId),
                    // Remove tag from all thread items
                    items: page.thread.items?.map((threadItem) => {
                      // Handle SMS message items
                      if (isSmsMessageItem(threadItem) && threadItem.smsMessage) {
                        return {
                          ...threadItem,
                          smsMessage: {
                            ...threadItem.smsMessage,
                            tags: threadItem.smsMessage.tags?.filter((currentTagId) => currentTagId !== tagId) || [],
                            tagsDetailed:
                              threadItem.smsMessage.tagsDetailed?.filter((tag) => tag.tagId !== tagId) || [],
                          },
                        };
                      }

                      // Handle call record items
                      if (isCallRecordItem(threadItem) && threadItem.callRecord) {
                        const updatedVoiceItemTags =
                          threadItem.callRecord.tags?.filter((tag) => tag.tagId !== tagId) || [];
                        return {
                          ...threadItem,
                          callRecord: {
                            ...threadItem.callRecord,
                            tags: updatedVoiceItemTags,
                          },
                        };
                      }

                      return threadItem;
                    }),
                  },
                };
              }),
            };
          },
        });
      });

      // Return revert function
      return {
        revertCacheUpdate: () => {
          // Restore original data for all affected queries
          originalQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'GetUnifiedThread', true>({
              endpointName: 'GetUnifiedThread',
              queryFilters: {
                queryKey,
                exact: true,
              },
              updater: () => originalData,
            });
          });
        },
      };
    },
    [queryUpdaters, isSmsMessageItem, isCallRecordItem]
  );

  const removeVoiceThreadItem = useCallback(
    ({ threadCallId, threadId, locationId }: { threadCallId: string; threadId?: string; locationId?: string }) => {
      // voicemail items are not stored in ListUnifiedThreads, so no need to update those queries

      // GetUnifiedThread updater

      // Store original GetUnifiedThread queries that will be modified
      const originalGetUnifiedThreadQueries: EndpointQueryData<'GetUnifiedThread'>[] = [];

      // predicate for GetUnifiedThread queries: filters queries by threadId/locationId and presence of the callRecord item
      const getUnifiedThreadPredicate = ({
        queryKey,
        state: { data },
      }: {
        queryKey: SchemaQueryKey<ServiceQueries, 'GetUnifiedThread'>;
        state: { data?: CachedEndpointData<'GetUnifiedThread'> };
      }) => {
        if (!data) return false;
        const request = queryKey[2] as GetUnifiedThreadIO['input'] | undefined;
        if (threadId && request?.threadId !== threadId) return false;
        if (locationId && request?.locationId !== locationId) return false;

        const threadContains = !!data.pages.some((page) =>
          page.thread?.items?.some(
            (item) => item && isCallRecordItem(item) && item.callRecord?.threadCallId === threadCallId
          )
        );

        if (threadContains) originalGetUnifiedThreadQueries.push([queryKey, data]);
        return threadContains;
      };

      // updater for GetUnifiedThread that removes matching callRecords from pages: filter out the voicemail item from items array based on threadCallId
      const getUnifiedThreadUpdater = (oldData: CachedEndpointData<'GetUnifiedThread'> | undefined) => {
        if (!oldData) return oldData as unknown as CachedEndpointData<'GetUnifiedThread'>;

        let changed = false;

        const updated = {
          ...oldData,
          pages: oldData.pages.map((page) => {
            if (!page.thread) return page;

            const newItems = page.thread.items?.filter(
              (item) => item && !(isCallRecordItem(item) && item.callRecord?.threadCallId === threadCallId)
            );

            if (!newItems || newItems.length === 0) {
              if (page.thread.items && page.thread.items.length > 0) changed = true;
              return {
                ...page,
                thread: {
                  ...page.thread,
                  items: newItems || [],
                },
              };
            }

            if (newItems.length !== (page.thread.items || []).length) changed = true;

            return {
              ...page,
              thread: {
                ...page.thread,
                items: newItems,
              },
            };
          }),
        };

        return changed ? updated : oldData;
      };

      queryUpdaters.updateQuery<'GetUnifiedThread', true>({
        endpointName: 'GetUnifiedThread',
        queryFilters: {
          predicate: getUnifiedThreadPredicate,
        },
        updater: getUnifiedThreadUpdater,
      });

      // GetUnifiedThreadDetails updater

      // Store original GetUnifiedThreadDetails queries that will be modified
      const originalGetUnifiedThreadDetailsQueries: EndpointQueryData<'GetUnifiedThreadDetails'>[] = [];

      // predicate for GetUnifiedThreadDetails queries: filters queries by threadId/locationId and presence of the callRecord item
      const getUnifiedThreadDetailsPredicate = ({
        queryKey,
        state: { data },
      }: {
        queryKey: SchemaQueryKey<ServiceQueries, 'GetUnifiedThreadDetails'>;
        state: { data?: CachedEndpointData<'GetUnifiedThreadDetails'> };
      }) => {
        if (!data || !data.thread) return false;
        const request = queryKey[2] as GetUnifiedThreadDetailsIO['input'] | undefined;
        if (threadId && request?.threadId !== threadId) return false;
        if (locationId && request?.locationId !== locationId) return false;

        const contains = !!data.thread.items?.some(
          (item) => item && isCallRecordItem(item) && item.callRecord?.threadCallId === threadCallId
        );

        if (contains) originalGetUnifiedThreadDetailsQueries.push([queryKey, structuredClone(data)]);
        return contains;
      };

      // update GetUnifiedThreadDetails cached queries to remove the voicemail item from their `items` array: filter out the callRecord item based on threadCallId
      const getUnifiedThreadDetailsUpdater = (oldData: CachedEndpointData<'GetUnifiedThreadDetails'> | undefined) => {
        if (!oldData || !oldData.thread) return oldData as unknown as CachedEndpointData<'GetUnifiedThreadDetails'>;

        const newItems = (oldData.thread.items || []).filter(
          (item) => item && !(isCallRecordItem(item) && item.callRecord?.threadCallId === threadCallId)
        );

        // If nothing changed, return the original to satisfy the updater signature
        if (newItems.length === (oldData.thread.items || []).length) return oldData;

        return {
          ...oldData,
          thread: {
            ...oldData.thread,
            items: newItems,
          },
        };
      };

      queryUpdaters.updateQuery<'GetUnifiedThreadDetails'>({
        endpointName: 'GetUnifiedThreadDetails',
        queryFilters: {
          predicate: getUnifiedThreadDetailsPredicate,
        },
        updater: getUnifiedThreadDetailsUpdater,
      });

      return {
        revertCacheUpdate: () => {
          originalGetUnifiedThreadQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'GetUnifiedThread', true>({
              endpointName: 'GetUnifiedThread',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });

          // Revert GetUnifiedThreadDetails changes
          originalGetUnifiedThreadDetailsQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'GetUnifiedThreadDetails'>({
              endpointName: 'GetUnifiedThreadDetails',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });
        },
      };
    },
    [queryUpdaters, isCallRecordItem]
  );

  const invalidateListUnifiedThreads = useCallback(() => {
    queryUpdaters.invalidateQueries({ endpointName: 'ListUnifiedThreads' });
  }, [queryUpdaters]);

  return {
    /**
     * A function to update the thread counts in the cache.
     * This currently only updates the counts for actionable and read status updates and is not comprehensive.
     * Add more status updates as needed.
     *
     * @param threadIds - The threadIds which have been affected by the status update
     * @param locationId - The locationId associated with the threads
     * @param newStatus - The new status to update the counts for: one of 'actionable', 'not-actionable', 'read', or 'unread'
     * @param isOptimistic - Whether this is an optimistic update
     */
    updateThreadCounts,
    /**
     * Updates or inserts a thread in the query cache.
     * Updates all SMSDataV4 queries that contain this thread.
     * Handles both manual and automated messages on the thread appropriately.
     *
     * @param matchValues - Values to use for matching the thread
     * @param matchFn - Function to match the thread to update
     * @param matchStrategy - Strategy for handling the provided match function and values
     * @param updater - Function that takes the existing thread and returns the updated thread
     * @param isOptimistic - Whether this is an optimistic update
     * @returns Object containing a function to revert the cache update
     */
    upsertUnifiedThread,
    /**
     * Updates or inserts a thread item (e.g., SMS message) in the query cache.
     * Updates all relevant queries, including ListUnifiedThreads and GetUnifiedThread.
     * Handles both creation of new thread items and updates to existing ones.
     * Provides a revert function to restore the original state in case of optimistic updates.
     *
     * @param options - The options for upserting the thread item
     * @param options.matchValues - Values to use for matching the thread and thread item
     * @param options.updater - A function that takes the existing thread item (if found) and returns the updated or new thread item
     * @param options.isOptimistic - Whether this is an optimistic update
     * @param options.getThreadOnly - If true, only fetches the thread without updating
     * @param options.updateOnly - If true, only updates existing thread items without creating new ones
     * @returns Object containing the result of the operation and the updated thread resource
     */
    upsertThreadItem,
    /**
     * Retrieves unified thread details from cache or fetches from server.
     * Searches through cached ListUnifiedThreads queries first, then falls back to GetUnifiedThreadDetails cache,
     * and finally makes a server request if not found. Supports filtering automated messages and validation.
     * This is the unified thread equivalent of the v4 getThreadDetails function.
     *
     * @param options - The options for retrieving thread details
     * @param options.threadId - The ID of the thread to retrieve
     * @param options.locationId - The ID of the location the thread belongs to
     * @param options.cacheOnly - If true, only searches in cache without making server requests
     * @param options.excludeLastAutomated - If true, excludes threads where the last message is automated
     * @param options.matchStrategy - Strategy for matching threads in cache
     * @param options.matchFn - Custom function for matching threads
     * @returns Promise that resolves to the unified thread or undefined if not found
     */
    getUnifiedThreadDetails,
    /**
     * Marks a thread item (e.g., SMS message) as soft deleted in the query cache.
     * Updates all relevant queries, including ListUnifiedThreads and GetUnifiedThread.
     * Handles both optimistic updates and provides a revert function to restore the original state.
     *
     * @param options - The options for deleting the thread item
     * @param options.threadItemId - The ID of the thread item to delete
     * @param options.threadId - The ID of the thread containing the thread item
     * @param options.locationId - The ID of the location the thread belongs to
     * @param options.deletedAt - The date and time the thread item was deleted (optional)
     * @param options.deletedBy - The user ID who deleted the thread item (optional)
     * @param options.statusDetails - The status details to set on the thread item (optional)
     * @param options.isOptimistic - Whether this is an optimistic update
     * @returns Object containing the result of the operation and a function to revert the cache update
     */
    deleteThreadItem,
    /**
     * Toggles the blocked status of messages from a person's phone number.
     * Updates all SMSDataV4 queries that contain affected threads.
     *
     * @param personPhone - Phone number of the person to block/unblock
     * @param isBlocked - Whether the phone number should be blocked
     * @param locationId - ID of the location for which to apply the block
     * @param isOptimistic - Whether this is an optimistic update
     * @returns Object containing a function to revert the cache update
     */
    togglePersonPhoneBlockUnified,
    /**
     * Updates all threads and messages in the query cache with new person information for the given phone numbers.
     * Associates the specified person details (personId, firstName, lastName, preferredName) with threads and SMS messages
     * that match any of the provided personPhones. Optionally, only updates threads or messages that do not already have a personId.
     *
     * @param personPhones - List of phone numbers to match
     * @param personId - The ID of the person to associate
     * @param firstName - The first name of the person
     * @param lastName - The last name of the person
     * @param preferredName - The preferred name of the person
     * @param locationId - The locationId the thread belongs to
     * @param onlyEmptyPersons - If true, only updates threads/messages without an existing personId
     */
    updateUnifiedThreadsWithNewPerson,
    /**
     * Updates threads with individual task operations in the query cache.
     * Updates all unified thread queries that contain the specified thread.
     * Supports both adding/updating and removing individual tasks.
     * Handles task status filtering and thread visibility changes automatically.
     *
     * @param threadId - The ID of the thread to update
     * @param locationId - The ID of the location the thread belongs to
     * @param task - The individual task to update or delete
     * @param action - The action to perform: 'UPDATE' (add/modify) or 'DELETE' (remove)
     * @param isOptimistic - Whether this is an optimistic update (optional)
     * @returns Object containing a function to revert the cache update
     */
    updateUnifiedThreadsTasksList,
    /**
     * Finds all voice items with a specific tag in GetUnifiedThread cache and emits call insights sync events.
     * This function searches through cached GetUnifiedThread queries that match the specified threadId and locationId,
     * and only processes queries that actually contain voice items (call records) with the specified tag.
     * For each matching voice item, it emits a 'callTagsExternalSync' event with the updated tag list
     * (after filtering out the specified tag). Returns a revert function that can restore the original
     * tag state by emitting sync events with the original tag lists.
     *
     * @param options - The options for emitting call insights sync
     * @param options.tagId - The ID of the tag to filter out and sync
     * @param options.threadId - The ID of the thread to match in cache queries
     * @param options.locationId - The location ID to match in cache queries
     * @returns Object containing a revertCacheUpdate function to restore original tag states
     */
    emitCallInsightsTagDismissSyncForVoiceItems,

    /**
     * Removes a tag from all thread items (SMS messages and call records) in GetUnifiedThread queries.
     * This function updates the cache for GetUnifiedThread queries by filtering out the specified tag
     * from all thread items that contain it. Returns a revert function that can restore the original
     * tag state by reverting all cache updates.
     *
     * @param matchValues - Object containing threadId and locationId to identify the thread
     * @param matchValues.threadId - The ID of the thread to update
     * @param matchValues.locationId - The location ID where the thread belongs
     * @param tagId - The ID of the tag to remove from all thread items
     * @returns Object containing a revertCacheUpdate function to restore original tag states
     */
    removeTagFromUnifiedThread,
    /**
     * Invalidate all cached `ListUnifiedThreads` queries so they refetch from the server.
     * Use this when a downstream change requires the list view to refresh from backend data.
     */
    invalidateListUnifiedThreads,
    /**
     * Hard-delete a voicemail (callRecord) ThreadItem from cache.
     *
     * This performs a hard delete in the client cache: it removes any ThreadItem whose
     * callRecord.threadCallId matches the provided `threadCallId` from both
     * `ListUnifiedThreads` query pages and `GetUnifiedThread` paginated queries.
     *
     * @param options.threadCallId - The threadCallId on the callRecord to remove
     * @param options.threadId - (optional) If provided, only affects threads with this id
     * @param options.locationId - (optional) If provided, only affects threads in this location
     * @returns Object containing a `revertCacheUpdate` function to restore original caches
     */
    removeVoiceThreadItem,
  };
};
